// Collection facets: checkbox change / filter-link click fetches the same URL through the Section Rendering API
// (?section_id=), swaps the tools bar, filter sheet, sidebar and grid, pushes history and keeps focus.
// Without JS the facets form is a native GET form with a visible Apply button.
const controllers = new WeakMap();
const SEL = '[data-collection]';
const SWAPS = ['tools', 'sheet', 'side', 'main', 'morebtn'];

export function init(root) {
  if (root.dataset.facetsInit) return;
  root.dataset.facetsInit = '1';
  const ac = new AbortController();
  const { signal } = ac;
  controllers.set(root, ac);
  root.classList.add('coll--js');

  let seq = 0;
  let inflight = null;
  let state = new URL(window.location.href); // the latest URL asked for (the DOM may lag behind it)
  async function load(href, { push, focusKey, focusFallback } = {}) {
    const my = ++seq;
    const url = new URL(href, window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    state = new URL(url.toString());
    if (inflight) inflight.abort(); // the latest request always wins
    inflight = new AbortController();
    const fsignal = inflight.signal;
    const fetchUrl = new URL(url.toString());
    fetchUrl.searchParams.set('section_id', root.dataset.sectionId);
    root.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(fetchUrl.toString(), { signal: AbortSignal.any([signal, fsignal]) });
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      if (my !== seq) return;
      const wasOpen = root.querySelector('.fsheet')?.open;
      for (const key of SWAPS) {
        const cur = root.querySelector(`[data-swap="${key}"]`);
        const next = doc.querySelector(`[data-swap="${key}"]`);
        if (cur && next) cur.innerHTML = next.innerHTML;
      }
      const stat = root.querySelector('[data-coll-status]');
      const nstat = doc.querySelector('[data-coll-status]');
      if (stat && nstat) stat.innerHTML = nstat.innerHTML; // live region itself is never replaced
      const more = root.querySelector('.more');
      const nmore = doc.querySelector('.more');
      if (more && nmore) more.hidden = nmore.hidden;
      const sheet = root.querySelector('.fsheet');
      if (sheet && wasOpen) sheet.open = true;
      if (push) window.history.pushState({ coll: 1 }, '', url.toString());
      let target = null;
      if (focusKey) target = root.querySelector(`[id="${CSS.escape(focusKey)}"]`);
      if (!target && focusFallback) target = root.querySelector(focusFallback);
      if (target) target.focus();
    } catch (err) {
      if (signal.aborted || fsignal.aborted) return; // superseded by a newer request
      window.location.assign(url.toString());
    } finally {
      if (my === seq) root.removeAttribute('aria-busy');
    }
  }

  root.addEventListener('change', (e) => {
    const box = e.target.closest('input[type=checkbox][data-url]');
    if (!box || !root.contains(box)) return;
    // Next URL = latest requested URL + this form's current checked state (so rapid clicks accumulate).
    const form = box.closest('form');
    const u = new URL(state.toString());
    const names = new Set([...form.querySelectorAll('input[type=checkbox]')].map((i) => i.name));
    names.forEach((n) => u.searchParams.delete(n));
    form.querySelectorAll('input[type=checkbox]:checked').forEach((i) => u.searchParams.append(i.name, i.value));
    const sort = form.querySelector('input[name=sort_by]');
    if (sort && !u.searchParams.has('sort_by')) u.searchParams.set('sort_by', sort.value);
    load(u.toString(), { push: true, focusKey: box.id });
  }, { signal });

  root.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('a[data-facet-link]');
    if (!a || !root.contains(a)) return;
    e.preventDefault();
    let href = a.getAttribute('href');
    if (a.dataset.clearPrice) { // drop only the price params, keep other filters and sort
      const u = new URL(state.toString());
      a.dataset.clearPrice.split(',').forEach((k) => u.searchParams.delete(k));
      href = u.toString();
    } else if (a.classList.contains('fband')) { // set the band, keep other filters and sort
      const u = new URL(state.toString());
      u.searchParams.delete('filter.v.price.gte');
      u.searchParams.delete('filter.v.price.lte');
      new URL(a.href, window.location.href).searchParams.forEach((v, k) => {
        if (k.startsWith('filter.v.price')) u.searchParams.set(k, v);
      });
      if (a.classList.contains('on')) { u.searchParams.delete('filter.v.price.gte'); u.searchParams.delete('filter.v.price.lte'); }
      href = u.toString();
    }
    load(href, { push: true, focusFallback: '.tools__f a, .facets input' });
  }, { signal });

  window.addEventListener('popstate', () => load(window.location.href, {}), { signal });
}

document.querySelectorAll(SEL).forEach(init);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll(SEL).forEach(init));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll(SEL).forEach((r) => { const c = controllers.get(r); if (c) c.abort(); });
});
